import React, { useEffect, useState } from "react";
import { api, formatError } from "../api";
import { useAuth } from "../context/AuthContext";
import { CalendarClock, Video, MapPin, Phone, X, Check, Link as LinkIcon } from "lucide-react";
import Avatar from "../components/ui/Avatar";

function fmt(iso) { try { return new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } }

const ModeIcon = ({ mode }) => mode === "onsite" ? <MapPin size={14} /> : mode === "phone" ? <Phone size={14} /> : <Video size={14} />;

export default function Interviews() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [msg, setMsg] = useState("");
  const isCompany = user?.role === "company";

  const load = async () => {
    try { const r = await api.get("/me/interviews"); setItems(r.data); } catch (e) { setMsg(formatError(e)); }
  };
  useEffect(() => { load(); }, []);

  const accept = async (iid, slot) => {
    try { await api.post(`/interviews/${iid}/accept`, { slot }); load(); } catch (e) { setMsg(formatError(e)); }
  };
  const cancel = async (iid) => {
    if (!window.confirm("Cancel this interview?")) return;
    try { await api.post(`/interviews/${iid}/cancel`); load(); } catch (e) { setMsg(formatError(e)); }
  };

  const scheduled = items.filter(i => i.status === "scheduled");
  const pending = items.filter(i => i.status === "proposed");
  const other = items.filter(i => !["scheduled","proposed"].includes(i.status));

  return (
    <div className="space-y-8">
      <div>
        <span className="eyebrow">Calendar & Interviews</span>
        <h2 className="font-display font-bold text-3xl mt-1">Interviews</h2>
        <p className="text-muted mt-1">{isCompany ? "Track proposals and confirmed interviews with applicants." : "Pending proposals and confirmed interviews."}</p>
      </div>

      {msg && <div className="card card-pad text-red-600 text-sm" data-testid="iv-msg">{msg}</div>}

      <Section label={`Pending · ${pending.length}`} testid="iv-pending">
        {pending.length === 0 ? <Empty text="No pending proposals." /> : pending.map(iv => (
          <Card key={iv.id} iv={iv} testid={`iv-${iv.id}`} isCompany={isCompany}>
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div className="flex-1 min-w-0">
                <div className="eyebrow">Proposed slots</div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {iv.slots.map(s => (
                    isCompany ? (
                      <span key={s} className="badge" data-testid={`iv-${iv.id}-slot-${s}`}>{fmt(s)}</span>
                    ) : (
                      <button key={s} onClick={() => accept(iv.id, s)}
                        className="btn-ghost" data-testid={`iv-${iv.id}-accept-${s}`}>
                        <Check size={14}/> {fmt(s)}
                      </button>
                    )
                  ))}
                </div>
                {iv.notes && <p className="text-sm text-muted mt-3">{iv.notes}</p>}
              </div>
              <button onClick={() => cancel(iv.id)} className="btn-ghost" data-testid={`iv-${iv.id}-cancel`}>
                <X size={14}/> Cancel
              </button>
            </div>
          </Card>
        ))}
      </Section>

      <Section label={`Scheduled · ${scheduled.length}`} testid="iv-scheduled">
        {scheduled.length === 0 ? <Empty text="No scheduled interviews yet." /> : scheduled.map(iv => (
          <Card key={iv.id} iv={iv} testid={`iv-${iv.id}`} isCompany={isCompany}>
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div className="flex-1">
                <div className="eyebrow">Scheduled for</div>
                <div className="font-display font-bold text-xl mt-1 inline-flex items-center gap-2">
                  <CalendarClock size={18} className="text-signal" /> {fmt(iv.accepted_slot)}
                </div>
                {iv.meeting_link && (
                  <a href={iv.meeting_link} target="_blank" rel="noreferrer"
                    className="mt-3 inline-flex items-center gap-1.5 text-sm text-signal font-semibold hover:underline"
                    data-testid={`iv-${iv.id}-link`}>
                    <LinkIcon size={14} /> Join meeting
                  </a>
                )}
                {iv.notes && <p className="text-sm text-muted mt-2">{iv.notes}</p>}
              </div>
              <button onClick={() => cancel(iv.id)} className="btn-ghost" data-testid={`iv-${iv.id}-cancel`}>
                <X size={14}/> Cancel
              </button>
            </div>
          </Card>
        ))}
      </Section>

      {other.length > 0 && (
        <Section label={`Past · ${other.length}`} testid="iv-past">
          {other.map(iv => (
            <Card key={iv.id} iv={iv} dimmed isCompany={isCompany}>
              <span className="badge">{iv.status}</span>
            </Card>
          ))}
        </Section>
      )}
    </div>
  );
}

function Section({ label, testid, children }) {
  return (
    <div data-testid={testid}>
      <div className="eyebrow mb-3">{label}</div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}
function Empty({ text }) {
  return <div className="card card-pad text-muted text-sm">{text}</div>;
}
function Card({ iv, dimmed, children, testid, isCompany }) {
  // Show the *counterpart* avatar: company sees student, student sees company.
  const avatarSrc = isCompany ? iv.student_photo_url : iv.company_photo_url;
  const avatarName = isCompany ? iv.student_name : iv.company_name;
  return (
    <div className={`card card-pad ${dimmed ? "opacity-70" : ""}`} data-testid={testid}>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Avatar src={avatarSrc} name={avatarName} size={44} testid={`iv-${iv.id}-avatar`} />
          <div className="min-w-0">
            <div className="font-display font-bold text-lg truncate">{iv.posting_title}</div>
            <div className="text-xs text-muted mt-0.5 truncate">
              {iv.company_name && <span>{iv.company_name} · </span>}
              {iv.student_name}
            </div>
          </div>
        </div>
        <span className="badge inline-flex items-center gap-1">
          <ModeIcon mode={iv.mode} /> {iv.mode}
        </span>
      </div>
      <div className="mt-4">{children}</div>
    </div>
  );
}
